/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';

import { ReactComponent as RemoveSvg } from 'assets/Icons/speaker-remove.svg';
import { Hint } from 'components/shared/Hint';
import { IRichTextInput } from 'components/shared/RichTextEditor/rich-text.model';
import { itemStyles, svgHoverShadow } from 'components/shared/twin.styles';
import { debounce, isArray, isEmpty, isEqual } from 'lodash';
import { MouseEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { Node, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import {
  addSpeaker,
  removeSpeaker,
  syncLatestSpeaker,
  updateCurrentSpeaker,
} from 'slices/player.slice';
import { Keys } from 'utils/enum';
import { ISpeakerRaw } from 'utils/models/transcript.model';
import { formatSpeaker } from 'utils/transcript.util';

import { Ripple } from '@rmwc/ripple';
import { RootState } from 'reducers';

type Props = {
  speaker: ISpeakerRaw;
  nodeData: IRichTextInput;
  toggleShowDropdown: (nextValue: boolean) => void;
  toggleDoubleClickSpeaker: (nextValue: boolean) => void;
  isGlobalSpeaker?: boolean;
};

export const SpeakerItem = ({
  speaker,
  nodeData,
  toggleShowDropdown,
  toggleDoubleClickSpeaker,
  isGlobalSpeaker,
}: Props) => {
  // console.count('RENDER SpeakerItem ---');
  const dispatch = useDispatch();

  const [isEditing, toggleEditing] = useToggle(false);
  const [newName, setNewName] = useState(speaker.name);

  const toggle = useSelector((state: RootState) => state.toggle);

  const speakerList = nodeData.data.speaker;
  const speakerName = formatSpeaker(
    isArray(speakerList) && !isEmpty(speakerList)
      ? speakerList?.[0]
      : speakerList,
  );

  const isSelected = (sp: ISpeakerRaw) =>
    isEqual(sp.name.toLowerCase(), speakerName.toLowerCase());

  const handleClickSpeaker = (e: React.MouseEvent) => {
    toggleDoubleClickSpeaker(false);
    toggleShowDropdown(false);
    e.stopPropagation();
    e.preventDefault();

    if (isSelected(speaker)) return;

    handleChangeSpeaker(speaker);
  };

  const debouncedSingleClick = debounce(handleClickSpeaker, 200);

  const handleUpdateSingleSpeakerItem = (speakerName: string) => {
    const path = ReactEditor.findPath(window.Editor, nodeData);
    const oldNode: any = Node.get(window.Editor, path);

    Transforms.setNodes(
      window.Editor,
      {
        data: { ...oldNode.data, speaker: speakerName },
      } as Partial<Node>,
      { at: path },
    );

    dispatch(syncLatestSpeaker());
    dispatch(addSpeaker(speakerName));
  };

  const handleKeyDownCurrentSpeaker = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.code === Keys.ESC) {
      setNewName(speaker.name);
      toggleEditing(false);
    }
    if (e.code === Keys.ENTER || e.code === Keys.ENTER_NUMPAD) {
      const name = newName?.trim();
      if (!isEmpty(name)) {
        const isApplyAll = toggle.isApplyToAllSpeaker;

        if (isApplyAll) {
          dispatch(
            updateCurrentSpeaker({
              oldName: speaker.name,
              newName: name,
              isOverWrite: true,
            }),
          );
        } else {
          handleUpdateSingleSpeakerItem(name);
        }
        toggleShowDropdown(false);
      }
      toggleEditing(false);
    }
  };

  const handleChangeSpeaker = (newSpeaker: ISpeakerRaw) => {
    // TODO: Replace with toggle variable
    const isApplyAll = toggle.isApplyToAllSpeaker;

    // Update all nodes with the new speaker
    if (isApplyAll) {
      dispatch(
        updateCurrentSpeaker({
          oldName: nodeData?.data?.speaker!,
          newName: newSpeaker.name,
        }),
      );

      dispatch(syncLatestSpeaker());
      // Retain last edited speaker
      dispatch(addSpeaker(newSpeaker.name));
      // Update single speaker only
    } else {
      handleUpdateSingleSpeakerItem(newSpeaker.name);
    }
  };

  const handleRemoveSpeaker = (speaker: ISpeakerRaw) => {
    dispatch(removeSpeaker(speaker));
  };

  const handleDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (isGlobalSpeaker) return;

    debouncedSingleClick.cancel();

    if (!isSelected(speaker)) {
      handleChangeSpeaker(speaker);
      toggleShowDropdown(false);
      return;
    }
    toggleEditing(true);
    setTimeout(() => {
      toggleDoubleClickSpeaker(true);
    }, 0);
  };

  return (
    <Ripple>
      <div
        css={[itemStyles, isSelected(speaker) && itemActiveStyles]}
        onClick={debouncedSingleClick}
      >
        {isEditing ? (
          <input
            tw="(height[3rem] pl-2 w-full min-width[unset] text-15 caret-sonnant-blue focus:(outline-none border-none))!"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDownCurrentSpeaker}
            maxLength={40}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <Hint text="Double click to rename speaker">
            <div
              tw="w-full"
              onDoubleClick={handleDoubleClick}
              className="line-clamp-1"
              title={speaker?.name?.length > 10 ? speaker.name : ''}
            >
              {speaker.name}
            </div>
          </Hint>
        )}

        {!speaker?.inUsed && (
          <span
            tw="p-0.5"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleRemoveSpeaker(speaker);
            }}
            css={removeIconCss}
          >
            <RemoveSvg
              className="disabled svg-shadow-hover"
              tw="mr-1.5"
              css={svgHoverShadow(0.2)}
            />
          </span>
        )}
      </div>
    </Ripple>
  );
};

const itemActiveStyles = css`
  ${tw`opacity-100 bg-disabled-lighter hover:(bg-disabled-lighter) font-medium`}
`;

const removeIconCss = css`
  &:hover {
    svg {
      opacity: 1;
      height: 1.1rem;
      width: 1.1rem;
      margin-right: 3px;
      ${svgHoverShadow(0.3)}
      path {
        fill: #211fcc !important;
      }
    }
  }
`;
